import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TrackeritemValidationFailuresModalComponent } from '../lib/trackerItemValidationsModal/trackeritem.validations.modal.component';
import { NotificationService } from '../../../core/services/core.notification.service';
import { DragNotificationService } from '../../../core/services/core.drag-notification.service';
import { TrackeritemRest } from '../services/trackeritem.rest';
import { TrackerService } from '../services/tracker.service';
import { TrackeritemService } from '../services/trackeritem.service';
import { UiService } from '../../../core/services/core.ui.service';
import { AdminService } from '../../../core/services/core.admin.service';
import { UserService } from '../../../core/services/core.user.service';
import { MonitorService } from '../../../core/services/core.monitor.service';
import { UploadService } from '../../../core/services/core.upload.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';
import { TrackerQueryService } from '../services/tracker.query.service';
import { MentionsService, MentionUser, MentionConfig } from '../../../core/services/core.mentio.service';
import { trackerMentionUsers } from '../services/trackeritem.mentions';
import { StateService } from '@uirouter/angular';
import { GfTitleService } from '../../../core/services/core.title.service';
import * as _ from 'lodash';

@Component({
  selector: 'trackeritem-add',
  template: require('./trackeritem.add.component.html')
})
export class TrackerItemAddComponent implements OnInit, OnDestroy, OnChanges {
  @Input() tracker;
  @Input() project;
  @Input() projectMenu;
  @Input() siteConfig;

  isReady: boolean = false;
  members: any[] = [];
  isLoaded: boolean = false;
  mode: string = 'add';
  filesUploading: any[] = [];
  maxUploadSize: number;
  autoSearchOn: boolean = false;
  trackerId: any;
  projectUnixName: string;
  tags: any[] = [];
  attachments: any[] = [];
  draggingOver: boolean = false;
  allowUpdate: boolean = true;
  monitoring: boolean = false;
  relatedSearch: string = '';
  relatedCount: any = { total: 0 };
  trackeritem: any = {};
  errors: any = {};
  uploadFiles: any[] = [];
  acls: any = {};
  showAutosearch: boolean;

  // The @-mention source for the details editor.
  mentions: MentionConfig | null = null;

  constructor(
    private modalService: NgbModal,
    private notificationService: NotificationService,
    private dragNotificationService: DragNotificationService,
    private trackeritemRest: TrackeritemRest,
    private trackerService: TrackerService,
    private trackeritemService: TrackeritemService,
    private uiService: UiService,
    private adminService: AdminService,
    private translateService: GfTranslateService,
    private userService: UserService,
    private monitorService: MonitorService,
    private uploadService: UploadService,
    private trackerQueryService: TrackerQueryService,
    private mentioService: MentionsService,
    private window: Window,
    private stateService: StateService,
    private sanitizer: DomSanitizer,
    private titleService: GfTitleService
  ) {}

  ngOnInit() {
    if (this.tracker) {
      this.initializeComponent();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.tracker && changes.tracker.currentValue && !this.isReady) {
      this.initializeComponent();
    }
  }

  ngOnDestroy() {
    this.notificationService.unsubscribe('ti-add');
    this.dragNotificationService.destroy();

    this.mentions?.destroy();

    // Revoke any object URLs created for local image previews
    this.filesUploading.forEach(f => {
      if (f.localPreviewUrl) {
        URL.revokeObjectURL(f.localPreviewUrl);
      }
    });
  }

  initializeComponent() {
    this.isReady = false;

    if (!this.tracker) {
      return;
    }

    const summary = _.truncate(this.translateService.translate('New Item in ') + this.tracker.trackerName, { length: 20 });
    this.titleService.setTitle(summary, this.tracker.project.name);

    this.setBlankTrackerItem();

    this.members = _.get(this.tracker, 'rel.assignees', []);
    this.isLoaded = true;
    this.maxUploadSize = this.uploadService.getMaxUploadSize('tracker');
    this.trackeritemService.generateTempTid();

    this.autoSearchOn = false;

    this.trackerId = this.stateService.params.tracker ? this.stateService.params.tracker : '';
    if (!this.trackerId.match(/^\d+$/)) {
      const msgStr = this.translateService.translate('Sorry, {{trackerItemId}} is not a valid Tracker Item ID', { 'trackerItemId': this.trackerId });
      this.uiService.message.showError(msgStr, 'Invalid Tracker Item ID');
      this.stateService.go('home', {}, {reload: true});
    }
    this.projectUnixName = this.stateService.params.unixName;

    if (!_.isEmpty(this.project) && _.has(this.project, 'rel.permissions.trackers')) {
      this.trackerService.acl.set(this.project.rel.permissions.trackers);
      this.acls = _.get(this.project, 'rel.permissions.trackers[' + this.trackerId + ']', {});
    }

    if (_.get(this.siteConfig, 'tiaAutosearch', true)) {
      this.adminService.getConfig('tracker', this.trackerId, 'autosearch', 1)
        .then(response => {
          if (Number(_.get(response, 'settingValue', 1))) {
            this.userService.preference.getPreference(`tiaAutosearch-${this.trackerId}`, 0)
              .then(response => {
                this.autoSearchOn = true;
                this.showAutosearch = (Number(_.get(response, 'preferenceValue', 1)) == 1) ? true : false;
              })
              .catch(() => {
                this.autoSearchOn = true;
                this.showAutosearch = true;
              });
          } else {
            this.autoSearchOn = false;
          }
        });
    }

    // Initializes drag notification service for drag and drop
    this.dragNotificationService.initializeDragListeners();
    this.initializeMentionConfigs();

    this.notificationService.subscribe('ti-add', 'DRAGOVER_START_EVENT', () => {
      this.draggingOver = true;
    });

    this.notificationService.subscribe('ti-add', 'DRAGOVER_END_EVENT', () => {
      this.draggingOver = false;
    });

    if (this.tracker.fields.sprint.isEnabled) {
      this.tracker.fields.sprint.fieldElements = this.trackerService.fields.getSprintsGroupedByStatus(this.tracker.fields.sprint);
    }
    for (const key of Object.keys(this.tracker.fields)) {
      const field = this.tracker.fields[key];
      if (field.fieldType === 8) {
        field.fieldElements = this.trackerService.fields.getReleasesGroupedByStatus(field);
        this.trackeritemService.setDefaultValueForReleaseField(this.trackeritem, field);
      }
    }

    const queryId: any = _.get(this.stateService.params, 'query', '');
    if (queryId !== '') {
      this.trackerQueryService.getQuery(queryId, this.tracker)
        .then(query => {
          this.trackerQueryService.mapFilterValueToFieldValue(this.trackeritem, this.tracker, query);
          this.trackeritem = _.cloneDeep(this.trackeritem);
        })
        .finally(() => {
          this.isReady = true;
        });
    } else {
      this.isReady = true;
    }
  }

  updateCallback(event: any) {
    if (!['summary', 'tags'].includes(event.fieldAlias)) return;
    this.relatedSearch = `${this.trackeritem.summary} ${this.tags}`;
  }

  setAutosearch(value) {
    this.showAutosearch = value;
    this.userService.preference.setPreference(`tiaAutosearch-${this.trackerId}`, value);
  }

  removeAttachment(id) {
    const attachment = this.uploadFiles[id];
    const uploading = this.filesUploading[id];
    let fileName = '';
    if (!_.isEmpty(attachment)) {
      fileName = attachment.name;
    }
    // Revoke object URL if one was created for a local preview
    if (uploading && uploading.localPreviewUrl) {
      URL.revokeObjectURL(uploading.localPreviewUrl);
    }
    this.uploadFiles.splice(id, 1);
    this.filesUploading.splice(id, 1);
    const msg = this.translateService.translate('Attachment {{fileName}} has been removed', { 'fileName': fileName });
    this.uiService.message.showSuccess(msg, 'Attachment Remove');
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer && event.dataTransfer.files) {
      for (var i = 0; i < event.dataTransfer.files.length; i++) {
        let file = event.dataTransfer.files[i];
        this.uploadFiles.unshift(file);
      }
      this.addFilesToUploading(event.dataTransfer.files);
    }
  }

  fileSelected(event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      for (var i = 0; i < input.files.length; i++) {
        var file = input.files[i];
        this.uploadFiles.unshift(file);
      }
      this.addFilesToUploading(input.files);
    }
  }

  resetFiles() {
    this.uploadFiles = [];
  }

  submitTrackerItem(nextLocation) {
    const saveAndNew = nextLocation === true;

    this.trackeritemService.save(this.trackeritem, { 
        stopRedirection: saveAndNew, 
        tmpRefId: this.trackeritemService.getTempTid() 
    })
    .then(response => {
        this.errors = {};
        const tid = response.id;

        if (this.monitoring) {
            this.monitorService.set('trackeritem', tid)
                .catch(() => {
                    this.monitoring = false;
                });
        }

        return this.submitAttachments(tid, _.cloneDeep(this.uploadFiles))
        .then(() => {
            if (!saveAndNew) {
                this.stateService.go('home.project.summary.tracker.browse', {tracker: this.trackerId});
            } else {
                // Saves fields to preserve
                const preservedValues = {
                    priority: this.trackeritem.priority,
                    sprint: this.trackeritem.sprint,
                    assignees: _.get(this.trackeritem, 'rel.assignees', [])
                };
                
                // Resets the form
                this.setBlankTrackerItem();

                // Fresh temp ref id so the next item's inline images are not
                // filed under the one the save above just consumed
                this.trackeritemService.generateTempTid();

                // Restores preserved values
                this.trackeritem.priority = preservedValues.priority;
                this.trackeritem.sprint = preservedValues.sprint;
                this.trackeritem.rel.assignees = preservedValues.assignees;
                
                // Clears the uploaded files
                this.filesUploading = [];
                this.uploadFiles = [];
                
                // Show success message
                this.uiService.message.showSuccess(
                    this.translateService.translate('Tracker item created successfully. You can create another one.')
                );
                
                // Reset any previous errors
                this.errors = {};
                
                // Scrolls to top of form
                window.scrollTo(0, 0);
            }
        });
    })
    .catch((error) => {
        const params = this.trackeritem.id ? { verb: 'save', title: 'Save' } : { verb: 'create', title: 'Create' };
        this.uiService.message.showError(
          this.translateService.translate("Sorry, could not {{verb}} the Tracker Item", params),
          this.translateService.translate("{{title}} Tracker Item", params)
        );
        this.errors = error;
        if (!_.isEmpty(this.errors)) {
          this.openTrackerItemValidationsModal(nextLocation);
        }
      });
  }

  saveCallback(params: any) {
    const trackeritem = params.trackeritem;
    const nextLocation = params.nextLocation
    this.trackeritem = trackeritem;
    this.submitTrackerItem(nextLocation);
  }

  private setBlankTrackerItem() {
    const defaults = {
      'priority': _.get(this, 'trackeritem.priority', 3)
    };

    // Preserve sprint and any existing assignees if they exist
    if (this.trackeritem?.sprint) {
      defaults['sprint'] = this.trackeritem?.sprint;
    }

    // Preserve existing assignees
    const existingAssignees = _.get(this, 'trackeritem.rel.assignees', []);

    this.trackeritem = this.trackeritemService.getBlankModel(this.tracker, this.trackeritem?.extraFields, defaults);
    
    // Preserve preserve assignees when "Save & New"
    if (existingAssignees.length > 0) {
      this.trackeritem.rel.assignees = existingAssignees;
    }

    // Clear specific fields when selected "Save & New"
    if (this.trackeritem.preserveFields) {
      const preserved = _.pick(this.trackeritem, ['priority', 'sprint', 'rel.assignees']);
      this.trackeritem = {
        ...this.trackeritem,
        ...preserved,
        summary: '',
        details: '',
        preserveFields: true
      };
    }

    this.attachments = [];
    this.filesUploading = [];
    this.uploadFiles = [];
    this.errors = {};
  }

  private addFilesToUploading(files) {
    for (const file of files) {
      const isImage = file.type && file.type.startsWith('image/');
      const localPreviewUrl = isImage ? URL.createObjectURL(file) : null;

      this.filesUploading.push({
        name: file.name,
        size: file.size,
        bytesUploaded: 0,
        bytesRemaining: file.size,
        percentComplete: 0,
        percentRemaining: 100,
        amountComplete: 0,
        amountTotal: file.size,
        queued: true,
        complete: false,
        isImage: isImage,
        localPreviewUrl: localPreviewUrl,
        safePreviewUrl: localPreviewUrl ? this.sanitizer.bypassSecurityTrustUrl(localPreviewUrl) : null
      });
    }
  }

  private submitAttachments(tid, _uploadFiles) {
    if (Array.isArray(_uploadFiles) && _uploadFiles.length > 0) {
      // Mark all queued files as uploading
      this.filesUploading.forEach(f => f.queued = false);

      const options = {
        successCallback: this.setAttachment.bind(this),
        progressCallback: this.progressCallback.bind(this),
        finishedCallback: this.removeAttachmentFromUploadsList.bind(this)
      };

      return this.uploadService.uploadFiles('trackeritem', tid, _uploadFiles, options);
    }

    return Promise.resolve();
  }

  private setAttachment(section, refId, file, hash) {
    const params = {
      fileName: file.name,
      fileType: (!_.isEmpty(file.type)) ? file.type : 'application/octet-stream'
    };

    return this.trackeritemRest.postAttachment(refId, hash, params)
      .then((response: any) => {
        if (_.has(response, 'lastModifiedDate')) {
          this.trackeritem.lastModifiedDate = response.lastModifiedDate;
        }
      });
  }

  private progressCallback(section, refId, file, bytesUploaded, bytesRemaining, percentUploaded, percentRemaining) {
    const fileInProgress = _.find(this.filesUploading, { name: file.name });

    if (!_.isEmpty(fileInProgress)) {
      fileInProgress.bytesUploaded = bytesUploaded;
      fileInProgress.bytesRemaining = bytesRemaining;
      fileInProgress.amountComplete = bytesUploaded;
      fileInProgress.amountTotal = file.size;
      fileInProgress.percentComplete = percentUploaded;
      fileInProgress.percentRemaining = percentRemaining;
    }
  }

  private removeAttachmentFromUploadsList(section, refId, file) {
    const fileInProgress = _.find(this.filesUploading, { name: file.name });
    if (fileInProgress) {
      fileInProgress.complete = true;
      fileInProgress.percentComplete = 100;
    }
  }

  private openTrackerItemValidationsModal(nextLocation) {
    var options = {
        nextLocation: nextLocation
    };

    const modalRef = this.modalService.open(TrackeritemValidationFailuresModalComponent);
    modalRef.componentInstance.trackeritem = this.trackeritem;
    modalRef.componentInstance.tracker = this.tracker;
    modalRef.componentInstance.tags = this.tags;
    modalRef.componentInstance.errors = this.errors;
    modalRef.componentInstance.onUpdate.subscribe(($event) => {
      modalRef.close();
      this.submitTrackerItem(options.nextLocation);
    });
    modalRef.componentInstance.close.subscribe(($event) => {
      modalRef.close();
    });
  }

  tagsUpdated($event) {
    this.trackeritem.tags = $event;
  }

  /**
   * Initialize mention configuration
   */
  initializeMentionConfigs(): void {
    this.mentions = this.mentioService.createMentionConfig({
      project: () => this.project?.unixName || '',
      seed: () => this.getTrackerUsers(),
      roles: () => this.project?.rel?.roles || []
    });
  }

  /**
   * Get users related to the tracker for mentions.
   *
   * A new item has no history or assignees of its own yet, so the project
   * members loaded for the submitter picker are this surface's contribution.
   */
  getTrackerUsers(): MentionUser[] {
    return trackerMentionUsers(this.mentioService, this.tracker, this.trackeritem, this.members || []);
  }

}
